import {
  IPolicyRuleComparator,
  IPolicyRuleCondition,
  IPolicyRuleConditionItem,
  IPolicySettingsField,
} from '@entities/policy';

import { DigitOperator, OPERATOR_COMPARATORS, RuleParam } from './policyRuleTypes';

const OPERATOR_TO_KEY = OPERATOR_COMPARATORS.reduce(
  (acc, { operator, key }) => ({ ...acc, [operator]: key }),
  {} as Record<DigitOperator, keyof IPolicyRuleComparator>,
);

const KEY_TO_OPERATOR = OPERATOR_COMPARATORS.reduce(
  (acc, { operator, key }) => ({ ...acc, [key]: operator }),
  {} as Record<string, DigitOperator>,
);

export const serializeParams = (items: RuleParam[]): IPolicyRuleCondition => {
  return items.reduce<IPolicyRuleCondition>((acc, item) => {
    if (item.type === 'multi-select') {
      acc[item.techname] = item.selected.map(option => option.value);
      return acc;
    }

    const numericValue = item.value === '' ? undefined : Number(item.value);
    if (numericValue === undefined || Number.isNaN(numericValue)) return acc;

    const key = OPERATOR_TO_KEY[item.operator];

    if (item.type === 'tag-digit-compare') {
      acc[item.techname] = {
        [key]: numericValue,
        tags: item.selected.map(option => option.value),
      };
      return acc;
    }

    acc[item.techname] = { [key]: numericValue };
    return acc;
  }, {});
};

export const buildParamsFromRule = (
  source: IPolicyRuleCondition | IPolicyRuleConditionItem[] | undefined,
  fields: IPolicySettingsField[],
): RuleParam[] => {
  if (!source) return [];

  const entries: Array<[string, IPolicyRuleCondition[string]]> = Array.isArray(source)
    ? source.map(item => [item.name, item.rawValue] as [string, IPolicyRuleCondition[string]])
    : Object.entries(source);

  return entries.map(([techname, value]) => {
    const field = fields.find(item => item.techname === techname);
    const label = field?.label ?? techname;

    if (Array.isArray(value)) {
      const valueMap = field?.values ?? {};
      const selected = value.map(item => ({
        value: item,
        label: valueMap[item] ?? item,
      }));
      return {
        techname,
        label,
        type: 'multi-select',
        values: valueMap,
        selected,
      };
    }

    const { tags, ...comparator } = value;
    const [opKey, numValue] = Object.entries(comparator)[0] ?? [];
    const operator = KEY_TO_OPERATOR[opKey] ?? '=';
    const valueString = numValue !== undefined ? String(numValue) : '';
    const isTagDigit = field?.type === 'tag-digit-compare' || Array.isArray(tags);

    if (isTagDigit) {
      const valueMap = field?.values ?? {};
      return {
        techname,
        label,
        type: 'tag-digit-compare',
        values: valueMap,
        selected: (tags ?? []).map(item => ({
          value: item,
          label: valueMap[item] ?? item,
        })),
        operator,
        value: valueString,
      };
    }

    return {
      techname,
      label,
      type: 'digit-compare',
      operator,
      value: valueString,
    };
  });
};
